import { clamp } from "./format";

// Default weights (sum=100). Sliders can change these.
// We include responseRate (requested in breakdown) as a small contributor.
export const defaultWeights = {
  confirmation: 45,
  cancellation: 30,
  returns: 20,
  response: 5,
};

export const normalizeWeights = (w) => {
  const sum = Object.values(w).reduce((a, b) => a + Number(b || 0), 0) || 1;
  const factor = 100 / sum;
  return {
    confirmation: Math.round(w.confirmation * factor),
    cancellation: Math.round(w.cancellation * factor),
    returns: Math.round(w.returns * factor),
    response: Math.round(w.response * factor),
  };
};

export const computeClientMetrics = (client) => {
  const orders = client?.orders || [];
  const totalOrders = orders.length || 0;

  const confirmed = orders.filter((o) => o.status === "confirmed").length;
  const cancelled = orders.filter((o) => o.status === "cancelled").length;
  const returned = orders.filter((o) => o.status === "returned").length;

  const responded = orders.filter((o) => o.responded).length;
  const delivered = orders.filter((o) => o.delivered).length;

  const revenue = orders
    .filter((o) => o.status === "confirmed")
    .reduce((sum, o) => sum + Number(o.amount || 0), 0);

  const safeDiv = (a, b) => (b ? (a / b) * 100 : 0);

  const confirmationRate = safeDiv(confirmed, totalOrders);
  const cancellationRate = safeDiv(cancelled, totalOrders);
  const returnRate = safeDiv(returned, totalOrders);
  const responseRate = safeDiv(responded, totalOrders);
  const deliveryRate = safeDiv(delivered, totalOrders);

  return {
    totalOrders,
    confirmed,
    cancelled,
    returned,
    responded,
    delivered,
    revenue,
    confirmationRate,
    cancellationRate,
    returnRate,
    responseRate,
    deliveryRate,
  };
};

// Scoring formula (requested) + response factor:
// score = confirmation_rate × w1
//       + (100 - cancellation_rate) × w2
//       + (100 - return_rate) × w3
//       + response_rate × w4
export const computeScore = (metrics, weights = defaultWeights) => {
  const w = normalizeWeights(weights);
  const confirm = clamp(metrics.confirmationRate, 0, 100);
  const cancel = clamp(metrics.cancellationRate, 0, 100);
  const ret = clamp(metrics.returnRate, 0, 100);
  const resp = clamp(metrics.responseRate, 0, 100);

  const score =
    (confirm * w.confirmation) / 100 +
    ((100 - cancel) * w.cancellation) / 100 +
    ((100 - ret) * w.returns) / 100 +
    (resp * w.response) / 100;

  return clamp(Math.round(score), 0, 100);
};

// export const scoreToColor = (score, client) => {
//   if (client?.blocked) return "black";
//   if (client?.manualRiskFlag && score < 80) return score >= 60 ? "red" : "black";
//   if (score >= 80) return "green";
//   if (score >= 60) return "blue";
//   if (score >= 35) return "red";
//   return "black";
// };
export const scoreToColor = (score) => {
  if (score < 25) return "black";
  if (score < 50) return "red";
  if (score < 75) return "blue";
  return "green";
};


export const scoreTooltip = (metrics, color, client) => {
  if (client?.blocked) return "Blocked client: repeated fake/refusal behavior.";
  const tips = [];

  if (metrics.confirmationRate >= 80) tips.push("High confirmation rate");
  if (metrics.confirmationRate < 50) tips.push("Low confirmation rate");

  if (metrics.cancellationRate >= 30) tips.push("Many cancellations");
  if (metrics.returnRate >= 20) tips.push("High returns");

  if (metrics.responseRate < 50) tips.push("Often unreachable");
  if (metrics.responseRate >= 80) tips.push("Good responsiveness");

  if (client?.manualRiskFlag) tips.push("Manual risk flag enabled");

  const map = {
    green: "Safe client",
    blue: "Medium risk",
    red: "Risky",
    black: "Very risky",
  };

  return `${map[color]} • ${tips.length ? tips.join(" • ") : "Balanced behavior"}`;
};

export const enrichClient = (client, weights = defaultWeights) => {
  const metrics = computeClientMetrics(client);
  const score = computeScore(metrics, weights);
  const color = scoreToColor(score, client);
  const tooltip = scoreTooltip(metrics, color, client);

  return {
    ...client,
    metrics,
    trust: { score, color, tooltip },
  };
};
